// AnalyticsDashboard.jsx - Admin analytics view for user action tracking
import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from '../../utils/logger';

const ACTION_OPTIONS = [
  'page_view',
  'search_query',
  'similarity_check',
  'find_copy_lookup',
  'source_click',
  'play_log',
  'play_log_edit',
  'play_log_delete',
  'collection_bucket_change',
  'bgg_import_start',
  'bgg_import_success',
  'bgg_import_failure',
  'jet_set_go_preferences',
  'jet_set_go_recommendations',
  'explore_search_select',
  'login',
  'logout',
  'anonymous_to_logged_in',
  'button_click',
];

function AnalyticsDashboard({ _user }) {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [retention, setRetention] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(null);
  const [playFrequency, setPlayFrequency] = useState(null);
  const [featureAdoption, setFeatureAdoption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [actionFilter, setActionFilter] = useState(ACTION_OPTIONS);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/summary?days=${parseInt(dateRange)}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      logger.error("Analytics summary fetch error:", err);
      setError("Failed to load analytics summary");
    }
  }, [dateRange]);

  const fetchEvents = useCallback(async () => {
    try {
      const url = `${API_BASE}/analytics/events?limit=200&days=${parseInt(dateRange)}`;
      const res = await httpRequest(url, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (err) {
      logger.error("Analytics events fetch error:", err);
    }
  }, [dateRange]);

  const fetchFunnel = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/funnel?days=${parseInt(dateRange)}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setFunnel(data.funnel);
    } catch (err) {
      logger.error("Analytics funnel fetch error:", err);
    }
  }, [dateRange]);

  const fetchRetention = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/retention`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setRetention(data);
    } catch (err) {
      logger.error("Analytics retention fetch error:", err);
    }
  }, []);

  const fetchSessionDuration = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/session-duration?days=${parseInt(dateRange)}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setSessionDuration(data);
    } catch (err) {
      logger.error("Analytics session-duration fetch error:", err);
    }
  }, [dateRange]);

  const fetchPlayFrequency = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/play-frequency?days=${parseInt(dateRange)}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setPlayFrequency(data);
    } catch (err) {
      logger.error("Analytics play-frequency fetch error:", err);
    }
  }, [dateRange]);

  const fetchFeatureAdoption = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/analytics/feature-adoption?days=${parseInt(dateRange)}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setFeatureAdoption(data);
    } catch (err) {
      logger.error("Analytics feature-adoption fetch error:", err);
    }
  }, [dateRange]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSummary(),
      fetchEvents(),
      fetchFunnel(),
      fetchRetention(),
      fetchSessionDuration(),
      fetchPlayFrequency(),
      fetchFeatureAdoption(),
    ]).finally(() => setLoading(false));
  }, [fetchSummary, fetchEvents, fetchFunnel, fetchRetention, fetchSessionDuration, fetchPlayFrequency, fetchFeatureAdoption]);

  const formatNumber = (n) => (n === null || n === undefined ? '-' : n.toLocaleString());

  const actionBreakdown = summary?.action_breakdown || {};
  const sortedActions = Object.entries(actionBreakdown)
    .sort((a, b) => b[1] - a[1]);

  const filteredEvents = actionFilter.length
    ? events.filter((e) => actionFilter.includes(e.action_type))
    : [];

  return (
    <div className="analytics-dashboard" style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '1rem' }}>Analytics Dashboard</h1>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <label>
          Date Range:{" "}
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </label>
        <label>
          Action Filter:{" "}
          <select
            multiple
            value={actionFilter}
            onChange={(e) =>
              setActionFilter(
                Array.from(e.target.selectedOptions).map((o) => o.value)
              )
            }
            style={{ minWidth: '160px', verticalAlign: 'top' }}
          >
            {ACTION_OPTIONS.map((action) => (
              <option key={action} value={action}>
                {action.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && <div>Loading analytics...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <StatCard label="Total Events" value={formatNumber(summary.total_events)} />
          <StatCard label="Unique Users" value={formatNumber(summary.unique_users)} />
          <StatCard label="Unique Sessions" value={formatNumber(summary.unique_sessions)} />
          <StatCard label="Avg Events / User" value={formatNumber(summary.avg_events_per_user)} />
        </div>
      )}

      {sortedActions.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Action Breakdown</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={{ padding: '0.5rem' }}>Action</th>
                <th style={{ padding: '0.5rem' }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {sortedActions.map(([action, count]) => (
                <tr key={action} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem' }}>{action}</td>
                  <td style={{ padding: '0.5rem' }}>{formatNumber(count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {funnel && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Conversion Funnel</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <StatCard label="Page Views" value={formatNumber(funnel.page_views)} />
            <StatCard label="Searches" value={formatNumber(funnel.searches)} />
            <StatCard label="Find a Copy" value={formatNumber(funnel.find_copy_lookups)} />
            <StatCard label="Play Logs" value={formatNumber(funnel.play_logs)} />
            <StatCard label="Collection Changes" value={formatNumber(funnel.collection_changes)} />
          </div>
        </div>
      )}

      {retention && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Retention</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <StatCard label="Total Users" value={formatNumber(retention.total_users)} />
            <StatCard label="Day-1 Rate" value={retention.retention?.day_1 ? `${(retention.retention.day_1.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Day-7 Rate" value={retention.retention?.day_7 ? `${(retention.retention.day_7.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Day-30 Rate" value={retention.retention?.day_30 ? `${(retention.retention.day_30.rate * 100).toFixed(1)}%` : '-'} />
          </div>
        </div>
      )}

      {sessionDuration && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Session Duration</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <StatCard label="Avg (seconds)" value={formatNumber(sessionDuration.avg_duration_seconds)} />
            <StatCard label="Median (seconds)" value={formatNumber(sessionDuration.median_duration_seconds)} />
          </div>
        </div>
      )}

      {playFrequency && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Play Logging Frequency</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <StatCard label="1 Session" value={formatNumber(playFrequency.distribution?.['1_session'])} />
            <StatCard label="2-5 Sessions" value={formatNumber(playFrequency.distribution?.['2_to_5_sessions'])} />
            <StatCard label="6-20 Sessions" value={formatNumber(playFrequency.distribution?.['6_to_20_sessions'])} />
            <StatCard label="20+ Sessions" value={formatNumber(playFrequency.distribution?.['20_plus_sessions'])} />
          </div>
        </div>
      )}

      {featureAdoption && (
        <div style={{ marginBottom: '2rem' }}>
          <h2>Feature Adoption</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <StatCard label="BGG Import" value={featureAdoption.features?.bgg_import ? `${(featureAdoption.features.bgg_import.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Jet Set Go" value={featureAdoption.features?.jet_set_go ? `${(featureAdoption.features.jet_set_go.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Marketplace" value={featureAdoption.features?.marketplace ? `${(featureAdoption.features.marketplace.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Play Log" value={featureAdoption.features?.play_log ? `${(featureAdoption.features.play_log.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Collection" value={featureAdoption.features?.collection ? `${(featureAdoption.features.collection.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Similarity" value={featureAdoption.features?.similarity ? `${(featureAdoption.features.similarity.rate * 100).toFixed(1)}%` : '-'} />
            <StatCard label="Explore" value={featureAdoption.features?.explore ? `${(featureAdoption.features.explore.rate * 100).toFixed(1)}%` : '-'} />
          </div>
        </div>
      )}

      <div>
        <h2>Recent Events</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={{ padding: '0.5rem' }}>Time</th>
                <th style={{ padding: '0.5rem' }}>Action</th>
                <th style={{ padding: '0.5rem' }}>Page</th>
                <th style={{ padding: '0.5rem' }}>User</th>
                <th style={{ padding: '0.5rem' }}>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((evt) => (
                <tr key={evt.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{new Date(evt.timestamp).toLocaleString()}</td>
                  <td style={{ padding: '0.5rem' }}>{evt.action_type}</td>
                  <td style={{ padding: '0.5rem', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{evt.page_url}</td>
                  <td style={{ padding: '0.5rem' }}>{evt.user_id ? evt.user_id.slice(0, 8) + '...' : 'anon'}</td>
                  <td style={{ padding: '0.5rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {evt.metadata ? JSON.stringify(evt.metadata) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredEvents.length === 0 && <div style={{ padding: '1rem', color: '#666' }}>No events found.</div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{
      padding: '1rem',
      borderRadius: '8px',
      border: '1px solid var(--border-color, #ddd)',
      background: 'var(--bg-secondary, #fafafa)',
    }}>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #666)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{value}</div>
    </div>
  );
}

export default AnalyticsDashboard;
